/**
 * ч.1357: список статзвітів до Держстату однієї форми (1-опт або 10-зез). Список —
 * серверна пагінація (Закон дизайну #1), статус подання — з журналу Кабінету респондента.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import {
  FORM_BY_SLUG, FORM_SHORT, FORM_TITLE, QUARTER_LABELS, SUB_STATUS_COLOR, SUB_STATUS_LABEL,
  statReports, statSubmissions, type StatFormSlug, type StatReport,
} from '@/api/statReports';
import { orgs as orgsApi } from '@/api/catalogs';
import { DocListFiltersRow } from '@/shared/components/DocListFilters';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

export default function StatReportsPage({ form }: { form: StatFormSlug }) {
  const navigate = useNavigate();
  const formCode = FORM_BY_SLUG[form];
  const { hasPermissionInAnyOrg, totalAccessibleOrgs } = usePermissionsByOrg('reports.stat.manage');
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgsApi.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgListQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgListQ.data]);
  const [orgId, setOrgId] = useOrgFilter();
  const period = useDefaultPeriod();
  const [fYear, setFYear] = useState<string>(() => period.dateFrom ? period.dateFrom.slice(0, 4) : '');
  const [fQuarter, setFQuarter] = useState<string>('');

  const { datasource, total } = useInfiniteGridDatasource<StatReport>(
    (limit, offset, sort) => statReports.list({
      organization_id: orgId || undefined, form_code: formCode,
      period_year: fYear ? Number(fYear) : undefined, period_quarter: fQuarter ? Number(fQuarter) : undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [orgId, fYear, fQuarter, formCode],
  );
  const subsQ = useQuery({ queryKey: ['stat-subs-all'], queryFn: () => statSubmissions.list({ limit: 500 }) });
  const subByDoc = useMemo(() => {
    const m = new Map<string, StatReport['id']>();
    const st = new Map<string, string>();
    for (const s of subsQ.data?.items ?? []) if (!st.has(s.report_id)) { st.set(s.report_id, s.status); m.set(s.report_id, s.id); }
    return st;
  }, [subsQ.data]);

  const cols = useMemo<ColDef<StatReport>[]>(() => [
    { colId: 'period', headerName: 'Період', width: 150,
      valueGetter: (p) => p.data ? `${QUARTER_LABELS[p.data.period_quarter]} ${p.data.period_year}` : '' },
    { field: 'document_number', headerName: '№', width: 220 },
    { field: 'organization_id', headerName: 'Організація', width: 240,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '' },
    { colId: 'zero', headerName: 'Даних', width: 110, sortable: false,
      valueGetter: (p) => p.data ? (p.data.fields?.zero_zvit ? 'немає' : 'є') : '' },
    { colId: 'submission', headerName: 'Подання', width: 200, sortable: false,
      cellRenderer: (p: { data?: StatReport }) => {
        if (!p.data) return null;
        const st = subByDoc.get(p.data.id) as keyof typeof SUB_STATUS_LABEL | undefined;
        const txt = st ? SUB_STATUS_LABEL[st] : '—';
        return <span style={{ color: st ? SUB_STATUS_COLOR[st] : 'var(--muted)', fontWeight: st === 'ACCEPTED' ? 600 : 400 }}>{txt}</span>;
      } },
    { field: 'created_at', headerName: 'Створено', width: 160,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '' },
  ], [subByDoc, orgName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{FORM_SHORT[formCode]}<TitleTotal total={total} /></h2>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{FORM_TITLE[formCode]}</span>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate(`/stat-reports/${form}/new`)}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg ? `Немає прав у жодній з ${totalAccessibleOrgs} організацій` : undefined}>
          + Створити за квартал
        </button>
      </div>
      <DocListFiltersRow>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 220, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgListQ.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select value={fQuarter} onChange={(e) => setFQuarter(e.target.value)} style={{ width: 150, flex: '0 0 auto' }}>
          <option value="">— квартал —</option>
          {[1, 2, 3, 4].map((q) => <option key={q} value={String(q)}>{QUARTER_LABELS[q]}</option>)}
        </select>
        <select value={fYear} onChange={(e) => setFYear(e.target.value)} style={{ width: 110, flex: '0 0 auto' }}>
          <option value="">— рік —</option>
          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + 1 - i).filter((y) => y >= 2025).map((y) => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>
      </DocListFiltersRow>
      <InfiniteDocGrid<StatReport>
        datasource={datasource}
        columnDefs={cols}
        storageKey={`stat-reports-${form}`}
        onRowDoubleClicked={(r) => navigate(`/stat-reports/${form}/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
